import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/layout/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Scissors,
  Clock,
  CheckCircle,
  Truck,
  Package,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";

interface WorkshopInsights {
  receivedToday: number;
  inProduction: number;
  readyForDelivery: number;
  avgQcTimeHours: number | null;
}

interface QueueOrder {
  id: string;
  publicId: string;
  customerName: string;
  itemCount: number;
  status: string;
  ageHours: number;
  createdAt: string;
}

export default function WorkshopDashboard() {
  const [insights, setInsights] = useState<WorkshopInsights | null>(null);
  const [productionQueue, setProductionQueue] = useState<QueueOrder[]>([]);
  const [qcQueue, setQcQueue] = useState<QueueOrder[]>([]);
  const [dispatchQueue, setDispatchQueue] = useState<QueueOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [insightsRes, ordersRes] = await Promise.all([
          fetch("/api/workshop/insights"),
          fetch("/api/workshop/orders"),
        ]);

        if (insightsRes.ok) {
          const data = await insightsRes.json();
          setInsights(data.insights);
        }

        if (ordersRes.ok) {
          const data = await ordersRes.json();
          const orders = data.orders || [];

          const production = orders
            .filter((o: any) => o.status === "IN_PRODUCTION")
            .slice(0, 5)
            .map((o: any) => ({
              id: o.id,
              publicId: o.publicId,
              customerName: o.customer?.name || "Unknown",
              itemCount: o.items?.length || 0,
              status: o.status,
              ageHours: getAgeHours(o.paidAt || o.createdAt),
              createdAt: o.createdAt,
            }));

          const qc = orders
            .filter((o: any) => o.status === "QC")
            .slice(0, 5)
            .map((o: any) => ({
              id: o.id,
              publicId: o.publicId,
              customerName: o.customer?.name || "Unknown",
              itemCount: o.items?.length || 0,
              status: o.status,
              ageHours: getAgeHours(o.paidAt || o.createdAt),
              createdAt: o.createdAt,
            }));

          const dispatch = orders
            .filter((o: any) => o.status === "READY_FOR_DELIVERY")
            .slice(0, 5)
            .map((o: any) => ({
              id: o.id,
              publicId: o.publicId,
              customerName: o.customer?.name || "Unknown",
              itemCount: o.items?.length || 0,
              status: o.status,
              ageHours: getAgeHours(o.readyForDeliveryAt || o.createdAt),
              createdAt: o.createdAt,
            }));

          setProductionQueue(production);
          setQcQueue(qc);
          setDispatchQueue(dispatch);
        }
      } catch (error) {
        console.error("Failed to fetch workshop data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  function getAgeHours(dateString: string): number {
    const date = new Date(dateString);
    const now = new Date();
    return Math.round((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  }

  function formatAge(hours: number): string {
    if (hours < 1) return "< 1h";
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) return `${days}d`;
    return `${days}d ${remainingHours}h`;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Workshop Dashboard"
        subtitle="Manage production, quality checks, and dispatch"
      />

      {loading ? (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-[100px]" />
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-[300px]" />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Received Today
                </CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {insights?.receivedToday ?? 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Orders arrived at workshop
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  In Production
                </CardTitle>
                <Scissors className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {insights?.inProduction ?? 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Active alterations
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Quality Check
                </CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{qcQueue.length}</div>
                <p className="text-xs text-muted-foreground">
                  Awaiting inspection
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Ready for Dispatch
                </CardTitle>
                <Truck className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {insights?.readyForDelivery ?? 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Awaiting delivery assignment
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Scissors className="h-4 w-4" />
                  Production Queue
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/workshop/production">View All</Link>
                </Button>
              </CardHeader>
              <CardContent>
                {productionQueue.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    No orders in production
                  </div>
                ) : (
                  <div className="space-y-3">
                    {productionQueue.map((order) => (
                      <Link
                        key={order.id}
                        to={`/workshop/orders/${order.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{order.publicId}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {order.customerName}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge
                            variant={order.ageHours > 48 ? "destructive" : "secondary"}
                          >
                            {formatAge(order.ageHours)}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {order.itemCount} item{order.itemCount !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  QC Queue
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/workshop/production">View All</Link>
                </Button>
              </CardHeader>
              <CardContent>
                {qcQueue.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    No orders awaiting QC
                  </div>
                ) : (
                  <div className="space-y-3">
                    {qcQueue.map((order) => (
                      <Link
                        key={order.id}
                        to={`/workshop/orders/${order.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{order.publicId}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {order.customerName}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline">Ready for QC</Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {order.itemCount} item{order.itemCount !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-4 w-4" />
                  Dispatch Queue
                </CardTitle>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/workshop/dispatch">View All</Link>
                </Button>
              </CardHeader>
              <CardContent>
                {dispatchQueue.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    No orders ready for dispatch
                  </div>
                ) : (
                  <div className="space-y-3">
                    {dispatchQueue.map((order) => (
                      <Link
                        key={order.id}
                        to={`/workshop/orders/${order.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg border hover:bg-accent transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">{order.publicId}</p>
                          <p className="text-sm text-muted-foreground truncate">
                            {order.customerName}
                          </p>
                        </div>
                        <div className="text-right">
                          {order.ageHours > 24 ? (
                            <Badge variant="destructive">
                              <AlertTriangle className="mr-1 h-3 w-3" />
                              {formatAge(order.ageHours)}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              {formatAge(order.ageHours)}
                            </Badge>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {order.itemCount} item{order.itemCount !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 sm:grid-cols-2">
                  <Button variant="outline" className="justify-start" asChild>
                    <Link to="/workshop/intake">
                      <Package className="mr-2 h-4 w-4" />
                      Receive Shipments
                    </Link>
                  </Button>
                  <Button variant="outline" className="justify-start" asChild>
                    <Link to="/workshop/quotes">
                      <Clock className="mr-2 h-4 w-4" />
                      Pending Quotes
                    </Link>
                  </Button>
                  <Button variant="outline" className="justify-start" asChild>
                    <Link to="/workshop/production">
                      <Scissors className="mr-2 h-4 w-4" />
                      Production Board
                    </Link>
                  </Button>
                  <Button variant="outline" className="justify-start" asChild>
                    <Link to="/workshop/dispatch">
                      <Truck className="mr-2 h-4 w-4" />
                      Schedule Dispatch
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Avg QC Time
                    </span>
                    <span className="font-medium">
                      {insights?.avgQcTimeHours != null
                        ? `${insights.avgQcTimeHours}h`
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Orders Received Today
                    </span>
                    <span className="font-medium">
                      {insights?.receivedToday ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Active Work Items
                    </span>
                    <span className="font-medium">
                      {(insights?.inProduction ?? 0) + qcQueue.length}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
